import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Alert,
  AlertDescription,
  Button,
  CircleQuestionmarkIcon,
  DataTable,
  type DataTableColumnDef,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  TooltipProvider,
  cn,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { SectionMfaSms, validateMfaPhoneNumber } from '~/app/pages/User/Personal/SectionMfaSms';
import { SectionMfaTotp } from '~/app/pages/User/Personal/SectionMfaTotp';
import { AddPasskeyButton, SectionPasskeys } from '~/app/pages/User/Personal/SectionPasskeys';
import { SectionResetPassword } from '~/app/pages/User/Personal/SectionResetPassword';
import { FormField, RenderFields, Section } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { tableDefaults } from '~/components/Table';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { inertWhenHidden } from '~/lib/components/selectors/primitives/inert';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { isAllowedAppAccess } from '~/lib/session/isAllowedAppAccess';
import { DashboardUser, PlatformRole, UserRepository } from '~/repositories';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';

import { EmailField } from './_components/EmailField';

interface CustomDashboardUser extends DashboardUser {
  temporaryData?: {
    authenticatorApp?: string;
    changePassword?: string;
    currentPassword?: string;
  };
}

interface PlatformRow {
  id: string;
  platformName: string;
  roleName: string;
  platformId: string;
  isOwner: boolean;
  isLiveEnabled: boolean;
}

const ICON_CLASS_NAME = '[--icon-size-x:16px] [--icon-size-y:16px]';

const AUTOFILL_CLASS_NAME =
  'autofill:[-webkit-text-fill-color:currentColor] autofill:[transition:background-color_9999s_ease-in-out_0s]';

const CurrentPasswordInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  return (
    <InputGroup className="h-10">
      <InputGroupInput
        type={isVisible ? 'text' : 'password'}
        placeholder="Current Password"
        aria-label="Current Password"
        autoComplete="current-password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn('px-4', AUTOFILL_CLASS_NAME)}
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          size="icon-xs"
          aria-label={isVisible ? 'Hide password' : 'Show password'}
          aria-pressed={isVisible}
          onClick={() => setIsVisible((visible) => !visible)}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          {isVisible ? <Icon.EyeClosed className={ICON_CLASS_NAME} /> : <Icon.Eye className={ICON_CLASS_NAME} />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
};

export const PagePersonal: React.FC = () => {
  const { currentPlatform, currentUser, refetchCurrentUser, switchPlatform, loginAuthMethod, mfaStatus } =
    useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler();
  const { openAlert } = useDialog();
  const deviceMFAEnabled = useFeatureFlag(FeatureFlag.EnableDeviceMFA);
  const passkeyAuthEnabled = useFeatureFlag(FeatureFlag.EnablePasskeyAuth);
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const executeReCAPTCHA = useReCAPTCHA();

  const formMethods = useForm<CustomDashboardUser>({
    defaultValues: {
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      phoneNumber: currentUser?.phoneNumber,
      email: currentUser?.email,
    },
  });

  const { handleSubmit, formState, reset } = formMethods;
  const { isDirty } = formState;

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  // Bumped after a successful password change to remount the password inputs, discarding the autofill
  // highlight 1Password leaves on the DOM node (clearing the value alone doesn't remove it).
  const [passwordResetKey, setPasswordResetKey] = useState<number>(0);

  // The header/footer "Save Changes" buttons stay disabled until there's something to save: a changed
  // profile field (react-hook-form dirty state) or a new password entered.
  const canSave = isDirty || newPassword.length > 0;

  const leavePlatformMutation = useMutation({
    mutationFn: async ({ platformId }: { platformId: string }) => {
      if (!currentUser) throw new Error('No user found');
      return UserRepository.removeUserFromPlatform({
        dashboardUserId: currentUser.id,
        platformId,
      });
    },
    onSuccess: async (_, { platformId }) => {
      const wasCurrentPlatform = currentPlatform?.id === platformId;

      await refetchCurrentUser();

      if (wasCurrentPlatform) {
        const { currentUser: updatedUser } = useSessionStore.getState();
        if (!updatedUser) {
          addDangerNotification({
            content: 'Failed to refresh user data',
          });
          return;
        }

        const ownerPlatform = updatedUser.platformRoles?.find((role: PlatformRole) => role.name === 'owner');

        if (ownerPlatform) {
          switchPlatform(ownerPlatform.platformId, !!useSessionStore.getState().isSandbox)
            .then(() =>
              executeReCAPTCHA(RecaptchaAction.UpdateUser, { optional: true })
                .then((reCaptchaToken) =>
                  updatedUser
                    .update({ defaultPlatformId: ownerPlatform.platformId }, reCaptchaToken)
                    .then(() => {
                      log({
                        name: 'Platform changed after leaving',
                        context: useSessionStore.getState().currentUser,
                      });
                    })
                    .catch((error) => {
                      addDangerNotification({
                        content: error.message,
                      });
                    })
                )
                .catch((err: Error) => {
                  addDangerNotification({
                    content: err.message,
                  });
                })
            )
            .catch((error) => {
              addDangerNotification({
                content: error.message || 'Failed to switch platform',
              });
            });
        }
      }

      addSuccessNotification({
        content: 'Successfully left platform',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to leave platform',
      });
    },
  });

  const handleLeavePlatform = useCallback(
    (platformName: string, platformId: string) => {
      openAlert({
        headline: `Leave ${platformName}?`,
        content: `You will lose all access to this platform and its associated data. Are you sure you want to leave ${platformName}?`,
        confirmButton: {
          label: 'Leave Platform',
          variant: 'danger',
        },
        onSuccess: () => {
          leavePlatformMutation.mutate({ platformId });
        },
      });
    },
    [openAlert, leavePlatformMutation]
  );

  const platformList: PlatformRow[] = useMemo(() => {
    if (!currentUser?.platformRoles) return [];
    return currentUser.platformRoles.map((platformRole) => ({
      id: platformRole.platformId,
      platformName: platformRole.platformName || 'Unknown Platform',
      roleName: platformRole.name,
      platformId: platformRole.platformId,
      isOwner: platformRole.name === 'owner',
      isLiveEnabled: platformRole.isLiveEnabled,
    }));
  }, [currentUser?.platformRoles]);

  const platformColumns: DataTableColumnDef<PlatformRow>[] = useMemo(
    () => [
      {
        header: 'Platform Name',
        accessor: 'platformName',
        width: 'minmax(300px, 1fr)',
        cell: ({ value }) => (
          <DataTable.Cell>
            <span className="text-sm font-medium text-foreground">{value}</span>
          </DataTable.Cell>
        ),
      },
      {
        header: (
          <button
            type="button"
            onClick={() => openHelpSidebar('Roles')}
            className="inline-flex cursor-pointer items-center gap-1 rounded-md transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <CircleQuestionmarkIcon />
            Role
          </button>
        ),
        accessor: 'roleName',
        width: 'min-content',
        cell: ({ value }) => (
          <DataTable.Cell>
            <span className="text-xs font-medium whitespace-nowrap text-muted-foreground">{getRoleLabel(value)}</span>
          </DataTable.Cell>
        ),
      },
      {
        header: 'Platform ID',
        accessor: 'platformId',
        width: 'minmax(312px, 330px)',
        cell: ({ value }) => (
          <DataTable.Cell className="overflow-visible">
            <Input
              variant="copy"
              inputSize="sm"
              readOnly
              showCopyButton
              aria-label="Platform ID"
              value={value}
              className={cn(
                '-ml-2 font-mono [&_input]:bg-transparent [&_input]:pl-2 [&_input]:text-xs [&_input]:shadow-none [&_input]:inset-shadow-none [&_input]:transition-shadow',
                'hover:[&_input]:inset-shadow-[0_0_0_1px_rgb(43_49_67/0.1)] [&_input]:focus-visible:inset-shadow-[0_0_0_1px_rgb(43_49_67/0.1)]',
                'dark:hover:[&_input]:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.1)] dark:[&_input]:focus-visible:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.1)]'
              )}
            />
          </DataTable.Cell>
        ),
      },
    ],
    [openHelpSidebar]
  );

  if (!currentUser) {
    return null;
  }

  // Clearing the New Password collapses the confirm/current fields, so wipe their values too rather
  // than leaving stale entries hidden in state.
  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    if (value.length === 0) {
      setConfirmPassword('');
      setCurrentPassword('');
    }
  };

  const handleSave = (data: CustomDashboardUser) => {
    if (isDirty) {
      const phoneNumberChanged = (data.phoneNumber ?? '') !== (currentUser.phoneNumber ?? '');
      executeReCAPTCHA(RecaptchaAction.UpdateUser, { optional: true })
        .then((reCaptchaToken) =>
          currentUser
            .update(
              {
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
              },
              reCaptchaToken
            )
            .then(() => {
              addSuccessNotification({
                content: 'User data updated',
              });
              // Rebase the form's dirty baseline onto the saved values so "Save Changes" disables again.
              reset(data);
              if (phoneNumberChanged) {
                void refetchCurrentUser();
              }
            })
            .catch((error) => {
              addDangerNotification({
                content: error.message,
              });
            })
        )
        .catch((err: Error) => {
          addDangerNotification({
            content: err.message,
          });
        });
    }

    if (newPassword) {
      if (!currentPassword) {
        addDangerNotification({
          content: 'Please enter your current password',
        });
        return;
      }

      if (newPassword !== confirmPassword) {
        addDangerNotification({
          content: 'Passwords are not the same',
        });
        return;
      }

      // The password endpoint is step-up gated, so the client interceptor launches the 2FA
      // confirmation modal and retries this request once the challenge succeeds.
      UserRepository.updatePassword({ currentPassword, newPassword })
        .then(() => {
          addSuccessNotification({
            content: 'Password changed',
          });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordResetKey((key) => key + 1);
        })
        .catch((error) => {
          // The user dismissed the 2FA modal: swallow the re-surfaced step-up error.
          if (isStepUpRequiredError(error)) {
            return;
          }
          addDangerNotification({
            content: error.message,
          });
        });
    }
  };

  const onSubmit = handleSubmit(handleSave, handleSubmitErrors);

  return (
    <TooltipProvider>
      <PageActions>
        <Button type="button" size="sm" disabled={!canSave} onClick={onSubmit}>
          Save Changes
        </Button>
      </PageActions>
      {currentPlatform && !isAllowedAppAccess(currentPlatform, mfaStatus, loginAuthMethod) && (
        <div className="px-(--page-margin) pt-6">
          <Alert
            variant="destructive"
            className="border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30 [&>svg]:[--icon-size-x:16px] [&>svg]:[--icon-size-y:16px]"
          >
            <Icon.Warning />
            <AlertDescription className="text-destructive">
              Additional verification is required to access this platform. Please enable two-factor authentication.
            </AlertDescription>
          </Alert>
        </div>
      )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={onSubmit}>
          {RenderFields<CustomDashboardUser>({
            sections: [
              {
                headline: 'Personal Information',
                fields: [
                  {
                    id: 'firstName',
                    label: 'First Name',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <Input
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="First Name"
                        aria-invalid={!!error}
                      />
                    ),
                  },
                  {
                    id: 'lastName',
                    label: 'Last Name',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <Input
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="Last Name"
                        aria-invalid={!!error}
                      />
                    ),
                  },
                  {
                    id: 'email',
                    label: 'Email address',
                    children: ({ value }) => <EmailField user={currentUser} value={String(value ?? '')} />,
                  },
                  {
                    content: (
                      <>
                        <FormField<CustomDashboardUser> id="temporaryData.changePassword" label="Update Password">
                          {() => (
                            <SectionResetPassword
                              key={passwordResetKey}
                              newPassword={newPassword}
                              confirmPassword={confirmPassword}
                              onNewPasswordChange={handleNewPasswordChange}
                              onConfirmPasswordChange={setConfirmPassword}
                            />
                          )}
                        </FormField>
                        <div
                          className={cn(
                            'grid transition-[grid-template-rows] duration-250 ease-out',
                            newPassword.length > 0 ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                          )}
                          {...inertWhenHidden(newPassword.length === 0)}
                        >
                          <div className="min-h-0 overflow-hidden">
                            <div className="pt-6">
                              <FormField<CustomDashboardUser>
                                id="temporaryData.currentPassword"
                                label="Current Password"
                              >
                                {() => (
                                  <CurrentPasswordInput
                                    key={passwordResetKey}
                                    value={currentPassword}
                                    onChange={setCurrentPassword}
                                  />
                                )}
                              </FormField>
                            </div>
                          </div>
                        </div>
                      </>
                    ),
                  },
                ],
              },
              {
                headline: 'Passkeys',
                description: 'Sign in to your account with secure, one-click authentication.',
                hide: !passkeyAuthEnabled,
                notifications: passkeyAuthEnabled && <AddPasskeyButton hideWhenEmpty />,
                content: passkeyAuthEnabled && <SectionPasskeys />,
              },
              {
                headline: 'Two-factor authentication',
                fields: [
                  {
                    id: 'phoneNumber',
                    label: 'Phone Number',
                    rules: { validate: (value) => validateMfaPhoneNumber(value, currentUser?.phoneNumber) },
                    children: ({ onChange, onBlur }, { error }) => (
                      <SectionMfaSms onPhoneNumberChange={onChange} onBlur={onBlur} error={error?.message} />
                    ),
                  },
                  {
                    id: 'temporaryData.authenticatorApp',
                    label: 'Authenticator App',
                    hide: !deviceMFAEnabled,
                    children: () => deviceMFAEnabled && <SectionMfaTotp />,
                  },
                ],
              },
            ],
          })}

          <div className="flex justify-end px-(--page-margin) pb-6">
            <Button type="submit" size="sm" disabled={!canSave}>
              Save Changes
            </Button>
          </div>
        </form>
      </FormProvider>
      {platformList.length > 0 && (
        <div className="shadow-[0_-1px_0_var(--color-border)]">
          <Section headline="Platforms">
            <DataTable<PlatformRow>
              {...tableDefaults}
              columns={platformColumns}
              data={platformList}
              rowKey={(row) => row.id}
              rowAction={(row) =>
                !row.isOwner ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    className="text-destructive dark:text-red-400"
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      handleLeavePlatform(row.platformName, row.platformId);
                    }}
                  >
                    Leave
                  </Button>
                ) : null
              }
            />
          </Section>
        </div>
      )}
    </TooltipProvider>
  );
};
